import { AxiosInstance } from "axios"
import { CardSubscription, DetailedPriceList, PriceListCreate } from "./priceListTypes"
import { ServiceResponse } from "../../../../core/api/apiTypes"

export enum PriceListQuery {
    LIST = "price-lists-for-merchant",
    DETAILS = "details-for-price-list",
    CARD_SUBSCRIPTIONS = "card-subscriptions",
}

export type PriceListType = {
    id: number
    name: string
    isActive: boolean
    messageToCustomers?: string
    internalNote?: string
    includeVat?: boolean
    // Upcoming feature: date when an inactive pricelist will become active (not yet returned by API)
    upcomingActivationDate?: string
    isResetProductSort?: boolean
}

// Sentinel id for the "No price list" filter option (services/products not assigned to any list).
export const NO_PRICE_LIST_ID = "NONE" as const
export type NoPriceListId = typeof NO_PRICE_LIST_ID

type FetchPriceListsParam = Readonly<{
    merchantId: number
    includeProducts?: boolean
    isActive?: boolean
}>

export type FetchPriceListsResponse = {
    priceLists: PriceListType[]
}

export async function fetchPriceLists(
    { merchantId, includeProducts, isActive }: FetchPriceListsParam,
    axios: AxiosInstance,
    signal?: AbortSignal,
): Promise<FetchPriceListsResponse> {
    const url = `/merchants/${merchantId}/priceLists`

    const response = await axios.get(url, {
        params: {
            isActive,
            includeProducts,
        },
        signal,
    })

    return response.data
}

type FetchDetailedPriceListsParam = Readonly<{
    id: number
}>

export type FetchDetailedPriceListsResponse = {
    priceList: DetailedPriceList
}

export async function fetchDetailedPriceLists(
    params: FetchDetailedPriceListsParam,
    axios: AxiosInstance,
    signal?: AbortSignal,
): Promise<FetchDetailedPriceListsResponse> {
    const { id } = params
    const url = `/priceLists/${id}`

    const response = await axios.get(url, {
        signal,
    })

    return response.data
}

type DeletePriceListParam = Readonly<{
    id: number
}>

type DeletePriceListResponse = Readonly<{}>

export async function deletePriceList(
    params: DeletePriceListParam,
    axios: AxiosInstance,
    signal?: AbortSignal,
): Promise<DeletePriceListResponse> {
    const { id } = params
    const url = `/priceLists/${id}`

    const response = await axios.delete(url, {
        signal,
    })

    return response.data
}

export async function createPriceList(
    priceList: PriceListCreate,
    axios: AxiosInstance,
    signal?: AbortSignal,
): Promise<ServiceResponse<{}>> {
    const url = "/priceLists"

    const response = await axios.post(
        url,
        {
            priceList,
        },
        {
            signal,
        },
    )

    return response.data
}

export async function updatePriceList(
    { priceList, priceListId }: { priceList: PriceListCreate; priceListId: number },
    axios: AxiosInstance,
    signal?: AbortSignal,
): Promise<ServiceResponse<{}>> {
    const url = `/priceLists/${priceListId}`

    const response = await axios.post(
        url,
        {
            priceList,
        },
        {
            signal,
        },
    )

    return response.data
}

type FetchIdCardsSubscriptionTypeParam = Readonly<{
    merchantId: number
}>

export type FetchIdCardsSubscriptionTypeResponse = {
    cardSubscriptions: Array<CardSubscription>
}

export async function fetchIdCardsSubscriptionType(
    { merchantId }: FetchIdCardsSubscriptionTypeParam,
    axios: AxiosInstance,
    signal?: AbortSignal,
): Promise<FetchIdCardsSubscriptionTypeResponse> {
    const url = `/merchants/${merchantId}/userCards/subscriptionTypes`

    const response = await axios.get(url, {
        signal,
    })

    return response.data
}

// -- Sort product in price list --

export type SortProductInPriceListBody = Readonly<{
    moveTo: {
        productGroup?: {
            id: number
        }
        position: number
    }
}>

type SortProductInPriceListParams = Readonly<{
    priceListId: number
    productId: number
    body: SortProductInPriceListBody
}>

export async function sortProductInPriceList(
    params: SortProductInPriceListParams,
    axios: AxiosInstance,
    signal?: AbortSignal,
): Promise<ServiceResponse<{}>> {
    const { priceListId, productId, body } = params
    const response = await axios.post(`/priceLists/${priceListId}/products/${productId}/sort`, body, { signal })
    return response.data
}

// -- Sort product group in price list --

export type SortProductGroupInPriceListBody = Readonly<{
    moveTo: {
        position: number
    }
}>

type SortProductGroupInPriceListParams = Readonly<{
    priceListId: number
    productGroupId: number
    body: SortProductGroupInPriceListBody
}>

export async function sortProductGroupInPriceList(
    params: SortProductGroupInPriceListParams,
    axios: AxiosInstance,
    signal?: AbortSignal,
): Promise<ServiceResponse<{}>> {
    const { priceListId, productGroupId, body } = params
    const response = await axios.post(`/priceLists/${priceListId}/productGroups/${productGroupId}/sort`, body, {
        signal,
    })
    return response.data
}

// Reset (initialize) product sort for a price list (products + groups + extra services)
type ResetProductSortParams = Readonly<{
    priceListId: number
}>

export async function resetProductSort(
    { priceListId }: ResetProductSortParams,
    axios: AxiosInstance,
    signal?: AbortSignal,
): Promise<ServiceResponse<{}>> {
    const response = await axios.post(`/priceLists/${priceListId}/resetProductSort`, undefined, { signal })
    return response.data
}
